import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import apiService from '../services/apiService';

const emptyLine = () => ({ title: '', description: '', quantity: 1, unit_price: 0 });

export default function EstimateTemplates() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: '',
    currency: 'GBP',
    lines: [emptyLine()],
  });

  async function fetchItems() {
    setError('');
    setLoading(true);
    try {
      const res = await apiService.get('estimate-templates');
      setItems(Array.isArray(res) ? res : []);
    } catch (e) {
      setError(e.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openCreate() {
    setEditingId(null);
    setForm({ name: '', currency: 'GBP', lines: [emptyLine()] });
    setDialogOpen(true);
  }

  async function openEdit(id) {
    setError('');
    try {
      const t = await apiService.get(`estimate-templates/${id}`);
      setEditingId(id);
      setForm({
        name: t.name || '',
        currency: t.currency || 'GBP',
        lines:
          (t.lines || []).length > 0
            ? t.lines.map((l) => ({
                title: l.title || '',
                description: l.description || '',
                quantity: l.quantity ?? 1,
                unit_price: l.unit_price ?? 0,
              }))
            : [emptyLine()],
      });
      setDialogOpen(true);
    } catch (e) {
      setError(e.message || 'Failed to load template');
    }
  }

  async function handleSave() {
    setError('');
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        currency: form.currency || 'GBP',
        lines: (form.lines || []).map((l) => ({
          title: l.title,
          description: l.description || undefined,
          quantity: l.quantity != null ? Number(l.quantity) : 1,
          unit_price: l.unit_price != null ? Number(l.unit_price) : 0,
        })),
      };
      if (!payload.name) {
        setError('Name is required');
        setBusy(false);
        return;
      }
      if (editingId) {
        await apiService.put(`estimate-templates/${editingId}`, payload);
      } else {
        await apiService.post('estimate-templates', payload);
      }
      setDialogOpen(false);
      setEditingId(null);
      await fetchItems();
    } catch (e) {
      setError(e.message || 'Failed to save template');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this template?')) return;
    setError('');
    try {
      await apiService.delete(`estimate-templates/${id}`);
      await fetchItems();
    } catch (e) {
      setError(e.message || 'Failed to delete');
    }
  }

  function updateLine(idx, patch) {
    setForm((f) => {
      const lines = [...(f.lines || [])];
      lines[idx] = { ...lines[idx], ...patch };
      return { ...f, lines };
    });
  }

  function addLine() {
    setForm((f) => ({ ...f, lines: [...(f.lines || []), emptyLine()] }));
  }

  function removeLine(idx) {
    setForm((f) => {
      const lines = [...(f.lines || [])];
      lines.splice(idx, 1);
      return { ...f, lines: lines.length ? lines : [emptyLine()] };
    });
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          Estimate templates
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none' }}>
          New template
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Reusable line bundles for new estimates (use “From template” on the Estimates screen).
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.length === 0 ? (
            <Card>
              <CardContent>
                <Typography sx={{ fontWeight: 800 }}>No templates yet.</Typography>
                <Typography variant="body2" color="text.secondary">
                  Create one with typical services and prices.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            items.map((it) => (
              <Card key={it.id} sx={{ boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900 }}>{it.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {it.currency || 'GBP'} · {it.lines_count ?? 0} line(s)
                    </Typography>
                  </Box>
                  <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEdit(it.id)} sx={{ textTransform: 'none' }}>
                    Edit
                  </Button>
                  <IconButton aria-label="Delete" onClick={() => handleDelete(it.id)}>
                    <DeleteIcon />
                  </IconButton>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => !busy && setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 800 }}>{editingId ? `Edit template #${editingId}` : 'New estimate template'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
            <TextField label="Currency" value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} fullWidth />
          </Box>

          <Typography sx={{ fontWeight: 800, mt: 2, mb: 1 }}>Lines</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {(form.lines || []).map((l, idx) => (
              <Card key={idx} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, gap: 1.5 }}>
                    <TextField label="Title" value={l.title} onChange={(e) => updateLine(idx, { title: e.target.value })} fullWidth size="small" />
                    <TextField
                      label="Qty"
                      type="number"
                      value={l.quantity}
                      onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                      fullWidth
                      size="small"
                      inputProps={{ step: '0.01', min: 0 }}
                    />
                    <TextField
                      label="Unit price"
                      type="number"
                      value={l.unit_price}
                      onChange={(e) => updateLine(idx, { unit_price: e.target.value })}
                      fullWidth
                      size="small"
                      inputProps={{ step: '0.01', min: 0 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <TextField
                      label="Description"
                      value={l.description}
                      onChange={(e) => updateLine(idx, { description: e.target.value })}
                      fullWidth
                      size="small"
                    />
                    <IconButton aria-label="Remove line" onClick={() => removeLine(idx)} size="small">
                      ×
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))}
            <Button variant="outlined" onClick={addLine} sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
              Add line
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={busy} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={busy} sx={{ textTransform: 'none' }}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
